import { useState, useEffect, useCallback, useRef, memo } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

function CustomCursorComponent() {
  // Early return for touch devices - check at render time
  const [isTouchDevice] = useState(() => {
    if (typeof navigator !== "undefined") {
      return navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches;
    }
    return false;
  });

  const [isVisible, setIsVisible] = useState(false);
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const rafRef = useRef<number | null>(null);
  const lastPosition = useRef({ x: -100, y: -100 });

  // Optimized spring config for smoother performance
  const springConfig = { damping: 25, stiffness: 500, mass: 0.5 };
  const cursorXSpring = useSpring(cursorX, springConfig);
  const cursorYSpring = useSpring(cursorY, springConfig);

  // Throttled mouse move handler using RAF
  const handleMouseMove = useCallback((e: MouseEvent) => {
    lastPosition.current = { x: e.clientX - 16, y: e.clientY - 16 };
    
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        cursorX.set(lastPosition.current.x);
        cursorY.set(lastPosition.current.y);
        rafRef.current = null;
      });
    }
    
    if (!isVisible) setIsVisible(true);
  }, [cursorX, cursorY, isVisible]);

  const handleMouseLeave = useCallback(() => setIsVisible(false), []);
  const handleMouseEnter = useCallback(() => setIsVisible(true), []);

  useEffect(() => {
    if (isTouchDevice) return;

    // Use passive listeners for better scroll performance
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseenter", handleMouseEnter);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isTouchDevice, handleMouseMove, handleMouseLeave, handleMouseEnter]);

  // Don't render anything for touch devices
  if (isTouchDevice) return null;

  return (
    <motion.div
      className="fixed top-0 left-0 w-8 h-8 rounded-full pointer-events-none z-[9999] mix-blend-difference bg-white"
      style={{
        translateX: cursorXSpring,
        translateY: cursorYSpring,
        opacity: isVisible ? 1 : 0,
        willChange: "transform",
        contain: "layout style paint",
      }}
    >
      {/* Simplified glow - single element instead of nested blur */}
      <div 
        className="absolute inset-[-4px] bg-white/30 rounded-full"
        style={{ filter: "blur(8px)" }}
      />
    </motion.div>
  );
}

// Memoize to prevent unnecessary re-renders
const CustomCursor = memo(CustomCursorComponent);
export default CustomCursor;